import { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Globe, Check, Loader2 } from 'lucide-react';

export default function LanguageSelector({ variant = 'default' }) {
  const { language, setLanguage, languages, translating } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const current = languages[language];
  const isLanding = variant === 'landing';

  return (
    <div className="relative" ref={ref} data-notranslate>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm transition-colors ${
          isLanding
            ? 'text-white/80 hover:text-white hover:bg-white/10'
            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
        }`}
        data-testid="language-selector"
        aria-label="Select language"
      >
        {translating ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Globe className="w-4 h-4" />
        )}
        <span className="hidden sm:inline">{current?.flag}</span>
        <span className="text-xs font-medium uppercase">{language}</span>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 z-[100] max-h-80 overflow-y-auto" data-notranslate>
          <div className="px-3 py-2 border-b border-gray-100">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Language</p>
          </div>
          {Object.entries(languages).map(([code, lang]) => (
            <button
              key={code}
              onClick={() => { setLanguage(code); setOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                language === code ? 'bg-emerald-50 text-emerald-700' : 'text-gray-700'
              }`}
              data-testid={`lang-${code}`}
            >
              <span className="text-base">{lang.flag}</span>
              <span className="flex-1 text-left font-medium">{lang.name}</span>
              {language === code && <Check className="w-4 h-4 text-emerald-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
